import React from 'react';
import PropTypes from 'prop-types';
import { propTypes } from '../../../util/types';

import { TAB_PRODUCTS, TAB_REVIEWS } from './ProfileNav';

// Import the ProfileNavListingsPanel and ProfileNavReviewsPanel components
import ProfileNavListingsPanel from './ProfileNavListingsPanel/ProfileNavListingsPanel';
import ProfileNavReviewsPanel from './ProfileNavReviewsPanel/ProfileNavReviewsPanel';

/**
 * Returns the appropriate content.
 *
 * @param {string} currentTab - The currently selected tab identifier.
 * @param {Object} props - The props object to pass to the tab component.
 * @return {JSX.Element} The JSX content for the specified tab, or the default content if the tab id is not recognized.
 */
const getTabContent = (currentTab, tabProps) => {
  const {
    currentUser,
    isCurrentUser,
    listings,
    queryListingsInProgress,
    queryListingsError,
    userReviews,
    fetchUserReviewsInProgress,
    fetchUserReviewsError,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    onSendReview,
    searchFiltersComponent,
    sortByComponent,
  } = tabProps;

  const tabComponents = {
    [TAB_PRODUCTS]: (
      <ProfileNavListingsPanel
        listings={listings}
        queryListingsInProgress={queryListingsInProgress}
        queryListingsError={queryListingsError}
        searchFiltersComponent={searchFiltersComponent}
        sortByComponent={sortByComponent}
      />
    ),
    [TAB_REVIEWS]: (
      <ProfileNavReviewsPanel
        currentUser={currentUser}
        isCurrentUser={isCurrentUser}
        userReviews={userReviews}
        fetchUserReviewsInProgress={fetchUserReviewsInProgress}
        fetchUserReviewsError={fetchUserReviewsError}
        sendReviewInProgress={sendReviewInProgress}
        sendReviewError={sendReviewError}
        reviewSubmitted={reviewSubmitted}
        onSendReview={onSendReview}
      />
    ),
  };

  return tabComponents[currentTab] || tabComponents[TAB_PRODUCTS];
};

const ProfileNavTabs = props => {
  const {
    currentTab,
    currentUser,
    isCurrentUser,
    // listings
    listings,
    queryListingsInProgress,
    queryListingsError,
    // reviews
    userReviews,
    fetchUserReviewsInProgress,
    fetchUserReviewsError,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    onSendReview,
    // filters
    searchFiltersComponent,
    sortByComponent,
  } = props;

  // Define the props by category
  const listingProps = {
    listings,
    queryListingsInProgress,
    queryListingsError,
  };
  const reviewProps = {
    currentUser,
    isCurrentUser,
    userReviews,
    fetchUserReviewsInProgress,
    fetchUserReviewsError,
    sendReviewInProgress,
    sendReviewError,
    reviewSubmitted,
    onSendReview,
  };
  const filterProps = {
    searchFiltersComponent,
    sortByComponent,
  };

  // Pass the necessary props to the tab component
  const tabProps = {
    ...listingProps,
    ...reviewProps,
    ...filterProps,
  };

  return getTabContent(currentTab, tabProps);
};

ProfileNavTabs.defaultProps = {
  listings: [],
  currentUser: null,
  isCurrentUser: false,
  queryListingsInProgress: false,
  queryListingsError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  reviewSubmitted: false,
  onSendReview: null,
  userReviews: [],
  fetchUserReviewsInProgress: false,
  fetchUserReviewsError: null,
  searchFiltersComponent: null,
  sortByComponent: null,
};

const { arrayOf, shape, bool, string, node, func } = PropTypes;

ProfileNavTabs.propTypes = {
  currentTab: string.isRequired,
  currentUser: propTypes.currentUser,
  isCurrentUser: bool.isRequired,
  listings: arrayOf(propTypes.listing),
  queryListingsInProgress: bool,
  queryListingsError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  reviewSubmitted: bool.isRequired,
  onSendReview: func.isRequired,
  userReviews: arrayOf(propTypes.userReview),
  fetchUserReviewsInProgress: bool.isRequired,
  fetchUserReviewsError: propTypes.error,
  searchFiltersComponent: shape({
    desktop: node.isRequired,
    mobile: node.isRequired,
  }),
  sortByComponent: node.isRequired,
};

export default ProfileNavTabs;
